import { Dialog, DialogContent, Grid, Pagination, TextField, Typography } from "@mui/material";
import { MprcCardActions, MprcContainer, MprcDialogTitleClose, MprcJoinComponents, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { MprcFilterCountInfo } from "components/filter";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { ManagedEnrollmentClassroomListResponse, ManagedEnrollmentClassroomListResponsePaginatedList, TrainingDueDateOption } from "generated/CoreApi_v1";
import { Formaters } from "lib/Formaters";
import TzUtils from "lib/TzUtils";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

////////////////////////////////////////////////////////////////////////////////

export interface IEnrollClassroomFilterOptions {
    searchText: string;
}

interface IViewDataFilter {
    pageNumber: number;
    filter: IEnrollClassroomFilterOptions;
}

interface IPros {
    trainingId: string;
    subtitle: string;
    onClose: (classroom?: ManagedEnrollmentClassroomListResponse) => void;
}

/////////////////////////////////////////////////////////////////////////////////

/**
 * Seleção de turmas para inscrição.
 * @param props
 */
export const EnrollmentClassroomDialog = (props: IPros) => {

    const { trainingId, subtitle, onClose } = props;

    const api = useCoreApi();
    const { t } = useTranslation("lms");

    const [loading, setLoading] = useState<boolean>(true);
    const [viewData, setViewData] = useState<ManagedEnrollmentClassroomListResponsePaginatedList>();
    const [viewError, setViewError] = useState<any>();
    const [searchText, setSearchText] = useState<string>("");

    const [viewFilter, setViewFilter] = useState<IViewDataFilter>({
        pageNumber: 1,
        filter: {
            searchText: "",
        },
    });

    const filtered = viewFilter.filter.searchText.length > 0;

    ///
    const handleFilter = (data: IViewDataFilter) => {
        setViewFilter(data);
        setLoading(true);
    };

    ///
    const resetFilters = () => {
        setSearchText("");
        setViewFilter({
            pageNumber: 1,
            filter: {
                searchText: "",
            },
        });
        setLoading(true);
    };

    ///
    useEffect(() => {
        async function load() {
            try {
                const result = await api.v1.enrollmentOperation.getManagedEnrollmentClassrooms({
                    trainingId,
                    searchText: viewFilter.filter.searchText,
                    pageNumber: viewFilter.pageNumber,
                    pageSize: 4,
                });
                setViewData(result.data);
            }
            catch (e) {
                setViewData(undefined);
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }
        if (loading) load();

    }, [loading]);

    ///
    const buildSecondLine = (item: ManagedEnrollmentClassroomListResponse) => {
        let c: React.ReactNode[] = [];

        if (item.enrollSuspended) {
            c.push(<Typography variant="bodyNotice">{t("Enrollment.StepTraining.ListTraining.EnrollmentSuspendedText")}</Typography>);

        } else if (item.executionStartDate != null && item.executionDueDate != null && item.executionStartDate == item.executionDueDate) {
            c.push(
                <Typography variant="caption" color="muted.main">
                    {
                        t("Enrollment.StepTraining.ListTraining.DateText", {
                            name: Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(item.executionStartDate, item.timezoneId)!, false)
                        })
                    }
                </Typography>
            );
        } else if (item.executionStartDate == null) {
            c.push(<Typography variant="caption" color="muted.main">{t("Enrollment.StepTraining.ListTraining.AnyTimeText")}</Typography>);
        } else if (item.executionStartDate != null) {
            c.push(
                <Typography variant="caption" color="muted.main">
                    {
                        t("Enrollment.StepTraining.ListTraining.StartDateText", {
                            name: Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(item.executionStartDate, item.timezoneId)!, false)
                        })
                    }
                </Typography>
            );
        }

        if (!item.enrollSuspended) {
            if (item.executionDueDateOption == TrainingDueDateOption.UntilDaysAfterStartTraining && item.executionDueDays) {
                c.push(
                    <Typography variant="caption" color="muted.main">
                        {t("Enrollment.StepTraining.ListTraining.UntilDaysAfterStartText", { count: item.executionDueDays })}
                    </Typography>
                );
            } else if (item.executionDueDateOption == TrainingDueDateOption.UntilSpecifedDate && item.executionDueDate && item.executionStartDate != item.executionDueDate) {
                c.push(
                    <Typography variant="caption" color="muted.main">
                        {
                            t("Enrollment.StepTraining.ListTraining.DueDateText", {
                                name: Formaters.momentFullDateTime(TzUtils.applyTimezoneOffsetFromUtc(item.executionDueDate, item.timezoneId)!, false)
                            })}
                    </Typography>
                );
            }

            if (item.availableEnrollmentsCount > 0) {
                c.push(
                    <Typography variant="caption" color="muted.main">
                        {t("Enrollment.StepTraining.ListTraining.AvailableEnrollmentText", { count: item.availableEnrollmentsCount })}
                    </Typography>
                );
            }
        }

        return <Typography variant="caption"><MprcJoinComponents components={c} separator={<span>&nbsp;&bull;&nbsp;</span>} /></Typography>;
    };

    ///
    const RenderContent = () => (
        <>
            {
                viewData && viewData.totalRows > 0 &&
                <Grid container justifyContent="center" alignItems="center" spacing={2}>
                    {
                        viewData!.items.map((item, index) => {
                            return (
                                <Grid key={index} alignItems="center" justifyContent="center" size={{ xs: 12 }}>
                                    <Grid container direction="row" spacing={1}>
                                        <Grid size={{ xs: 12 }}>
                                            <MprcListItemTitle onClick={() => onClose(item)}>
                                                {item.name}
                                            </MprcListItemTitle>
                                        </Grid>
                                        <Grid size={{ xs: 12 }} sx={(theme) => ({ mt: theme.spacing(-.5) })}>
                                            {buildSecondLine(item)}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            );
                        })
                    }
                </Grid>
            }
        </>
    );

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <Dialog
            open
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    minHeight: 450
                }
            }}
            onClose={() => onClose()}
        >
            <MprcDialogTitleClose
                title={t("Enrollment.StepOperation.SelectClassroom.Title")}
                subtitle={subtitle}
                onClose={() => onClose()}
                shrinkPadding
            />

            <DialogContent>
                <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

                <MprcRequestIndicator type="request" visible={loading} />

                <TextField
                    fullWidth
                    autoFocus
                    placeholder={t("Enrollment.StepOperation.SelectClassroom.SearchTextPlaceholder")}
                    autoComplete="off"
                    value={searchText}
                    onChange={(e) => setSearchText(e.currentTarget.value)}
                    onKeyDown={(e) => { if (e.keyCode == 13) { handleFilter({ filter: { searchText }, pageNumber: 1 }); } }}
                />

                {
                    !loading && viewData &&
                    <>
                        <MprcContainer>
                            <MprcFilterCountInfo
                                text={t(filtered ? "Enrollment.StepOperation.SelectClassroom.RecordsFoundText" : "Enrollment.StepOperation.SelectClassroom.RecordsText", { count: viewData.totalRows })}
                                onResetFilter={filtered ? resetFilters : undefined}
                                variant={filtered ? "bodyNotice" : viewData.totalRows == 0 ? "captionHeader" : "caption"}
                            />
                        </MprcContainer>

                        <RenderContent />
                    </>
                }
            </DialogContent>
            <MprcCardActions>
                {
                    viewData && viewData.totalPages > 1 && (
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={viewData.pageNumber}
                            count={viewData.totalPages}
                            onChange={(e, pageNumber) => {
                                if (pageNumber != viewFilter.pageNumber) {
                                    setViewFilter({ ...viewFilter, pageNumber });
                                    setLoading(true);
                                }
                            }}
                        />
                    )
                }
            </MprcCardActions>
        </Dialog>
    );
};
